'use client';

import { Divider, IconButton } from '@ads/components-react';
import { ArrowLeft, CalendarDays, QuestionMarkCircle } from '@ads/icons';
import { cn } from '@linka/utils';
import type { ReactNode } from 'react';
import { Skeleton } from '../skeleton/skeleton';

export interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  variant?: 'brand' | 'event';
  logoHref?: string;
  renderLogoLink?: (props: { href: string; 'aria-label': string; children: ReactNode }) => ReactNode;
  areaTitle?: string;
  supportLabel?: string;
  onSupportClick?: () => void;
  menu?: ReactNode;
  isLoading?: boolean;
  backLabel?: string;
  onBack?: () => void;
  banner?: ReactNode;
  eventName?: string;
  eventDate?: string;
  'data-testid'?: string;
}

export const Header = ({
  variant = 'brand',
  className,
  logoHref = '/',
  renderLogoLink,
  areaTitle,
  supportLabel,
  onSupportClick,
  menu,
  isLoading,
  backLabel,
  onBack,
  banner,
  eventName,
  eventDate,
  ...rest
}: HeaderProps) => (
  <header
    className={cn('flex items-center justify-between w-full h-xxl bg-ctx-layout-surface px-xxxs', className)}
    {...rest}
  >
    {variant === 'event' ? (
      <EventContent
        backLabel={backLabel}
        onBack={onBack}
        banner={banner}
        eventName={eventName}
        eventDate={eventDate}
        isLoading={isLoading}
      />
    ) : (
      <div className='flex items-center gap-nano'>
        {(renderLogoLink ?? defaultRenderLogoLink)({
          href: logoHref,
          'aria-label': 'Linka Eventos',
          children: (
            <>
              <img src='/svg/logo-linka-xxl-horizontal.svg' alt='Linka Eventos' className='w-[6.937rem] h-[3.062rem]' />
            </>
          ),
        })}

        {areaTitle && (
          <div className='flex items-center gap-mini self-stretch'>
            <Divider aria-hidden orientation='vertical' custom={{ root: 'self-stretch !h-auto' }} />
            <span className='ts-heading-xs text-ctx-content-title'>{areaTitle}</span>
          </div>
        )}
      </div>
    )}

    <div className='flex items-center gap-mini shrink-0'>
      <IconButton
        ariaLabel={supportLabel ?? ''}
        icon={QuestionMarkCircle}
        hierarchy='tertiary'
        onClick={onSupportClick}
        size='md'
      />
      {menu}
    </div>
  </header>
);

const defaultRenderLogoLink: NonNullable<HeaderProps['renderLogoLink']> = ({ children, ...props }) => (
  <a {...props}>{children}</a>
);

interface EventContentProps
  extends Pick<HeaderProps, 'backLabel' | 'onBack' | 'banner' | 'eventName' | 'eventDate' | 'isLoading'> {}

const EventContent = ({ backLabel, onBack, banner, eventName, eventDate, isLoading }: EventContentProps) => (
  <div className='flex items-center gap-nano min-w-0'>
    <IconButton
      ariaLabel={backLabel ?? ''}
      icon={() => <ArrowLeft className='text-ctx-content-primary size-xxs' strokeWidth={1} />}
      hierarchy='tertiary'
      onClick={onBack}
      size='md'
    />
    {isLoading ? (
      <div className='flex items-center gap-quark'>
        <Skeleton className='h-sm w-xl rounded-sm shrink-0' />
        <div className='flex flex-col gap-quark'>
          <Skeleton className='h-xxs w-xxl' />
          <Skeleton className='h-xxs w-lg' />
        </div>
      </div>
    ) : (
      <div className='flex items-center gap-nano'>
        {banner ?? <Skeleton className='h-sm w-xl rounded-sm shrink-0' />}
        <div className='flex flex-col w-[9.375rem] md:w-auto'>
          {eventName && (
            <strong className='ts-heading-xxs text-ctx-content-title truncate' title={eventName}>
              {eventName}
            </strong>
          )}
          {eventDate && (
            <div className='flex items-center gap-quark'>
              <CalendarDays className='text-ctx-content-base/semiOpaque size-xxs' />
              <span className='ts-caption-xxxs text-ctx-content-base/semiOpaque truncate relative top-[0.125rem]'>
                {eventDate}
              </span>
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);
